import { decodeBase64Url } from "utils";

import TeamsAuthClient from "../Services/Teams/TeamsAuthClient";
import {Authentication} from "../Types/teams";

export const SESSION_KEY = "x-leanspace-auth-session";

const TOKEN_REFRESH_LEEWAY_SECONDS = 30;

let inFlightRefresh: Promise<string> | null = null;

export const getFreshJWT = async (): Promise<string> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = (async () => {
    const session = getSession();
    if (!session) {
      throw new Error("[Unexpected] Session doesn't exist");
    }

    let authentication: Authentication;
    try {
      authentication = await TeamsAuthClient.refresh(
        session.email,
        session.refresh_token
      );
    } catch (error) {
      clearSession();
      window.location.reload();
      throw error;
    }
    setSession({...authentication, email: session.email});
    return authentication.access_token;
  })();

  try {
    return await inFlightRefresh;
  } finally {
    inFlightRefresh = null;
  }
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const json = decodeBase64Url(parts[1]);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const shouldRefreshToken = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  if (typeof exp !== "number") {
    return false;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return exp - nowInSeconds <= TOKEN_REFRESH_LEEWAY_SECONDS;
};

export const getValidAccessToken = async (): Promise<string> => {
  const session = getSession();
  if (!session) {
    throw new Error("[Unexpected] Session doesn't exist");
  }

  if (shouldRefreshToken(session.access_token)) {
    return getFreshJWT();
  }

  return session.access_token;
};

export const getSession = (): Authentication & { email: string } | undefined => {
  const session = localStorage.getItem(SESSION_KEY);
  if (!session) {
    return undefined;
  }
  return JSON.parse(session);
}

export const setSession = (session: Authentication & { email: string }): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export const clearSession = (): void => {
  localStorage.removeItem(SESSION_KEY);
}
